import angular, { IPromise } from 'angular'
import { trackError } from '@vend/utilities'
import {
  ServiceAgenda,
  ServiceItemResponseData,
  ServiceWithAgenda,
} from '../models/sale.constants'
import { client } from '../apollo'
import { getServiceById, serviceAgendaBySaleIdQuery } from '../queries/services'

/**
 * Responsible for managing Service Sales (work services)
 */
export class ServicesManager {
  constructor(wrDebugTools: any) {
    'ngInject'
    wrDebugTools.register('servicesManager')
  }

  /**
   * Get service agenda using the sale id
   *
   * @param saleId String
   *        The ID of the sale containing the service agenda
   *
   * @returns {Promise} A promise resolved once the service agenda has been retrieved
   *
   */
  public getServiceAgendaBySaleId(saleId: string): IPromise<ServiceWithAgenda> {
    return client
      .query({
        query: serviceAgendaBySaleIdQuery,
        variables: { saleId },
      })
      .then(response =>
        this._handleGetServiceAgendaBySaleIdSuccess(saleId, response.data)
      )
      .catch(response =>
        this._handleGetServiceAgendaBySaleIdFailure(
          saleId,
          response.data,
          response.status
        )
      )
  }

  /**
   * Handle a successful service agenda retrieval
   * @param saleId String
   *       The ID of the sale containing the service agenda
   *
   * @param data {Any}
   *      The data or error message returned by the server
   *
   * @returns {Promise} A promise resolved once the service agenda has been retrieved
   *
   */

  private _handleGetServiceAgendaBySaleIdSuccess(
    saleId: string,
    data: $TSFixMe
  ): IPromise<ServiceWithAgenda> {
    if (data.listServices?.services?.length > 0) {
      return Promise.resolve<ServiceWithAgenda>({
        // JAWS supports multiple services per sale, but the current implementation only support one
        id: data.listServices.services[0].id,
        status: data.listServices.services[0].status,
        agenda: data.listServices.services[0].agenda as ServiceAgenda,
      })
    }
    return this._handleGetServiceAgendaBySaleIdFailure(saleId, data)
  }

  /**
   *  Handle a failure when getting service agenda by sale id
   *
   * @param saleId String
   *      The ID of the sale containing the service agenda
   * @data {Any}
   *     The data or error message returned by the server
   * @return {Promise} A rejected promise with the error message
   *
   */
  private _handleGetServiceAgendaBySaleIdFailure(
    saleId: string,
    data: $TSFixMe,
    status?: number
  ): IPromise<never> {
    const message = `[ServicesManager] Services: Failed to retrieve service agenda`
    trackError(message, {
      data: {
        saleId,
        responseCode: status,
        data,
      },
    })
    return Promise.reject(new Error(`${message}`))
  }

  /**
   * Get service item by service id
   *
   * @param serviceId String
   * @returns {Promise} A promise resolved once the service item has been retrieved
   */
  public getServiceByServiceId(
    serviceId: string
  ): IPromise<ServiceItemResponseData> {
    return client
      .query({
        query: getServiceById,
        variables: { serviceId },
      })
      .then(response =>
        this._handleGetServiceByServiceIdSuccess(serviceId, response.data)
      )
      .catch(response =>
        this._handleGetServiceByServiceIdFailure(
          serviceId,
          response.data,
          response.status
        )
      )
  }

  /**
   * @param serviceId String
   *        The ID of the service containing the service item
   * @param data {Any}
   *        The data or error message returned by the server
   * @returns  {Promise} A  promise resolved once the service item has been retrieved
   */
  private _handleGetServiceByServiceIdSuccess(
    serviceId: string,
    data: $TSFixMe
  ): IPromise<ServiceItemResponseData> {
    if (data.getService) {
      return data.getService
    }
    return this._handleGetServiceByServiceIdFailure(serviceId, data)
  }

  /**
   *  Handle a failure when getting service item by service id
   *
   * @param serviceId String
   *      The ID of the service containing the service item
   * @data {Any}
   *     The data or error message returned by the server
   * @return {Promise} A rejected promise with the error message
   *
   */
  private _handleGetServiceByServiceIdFailure(
    serviceId: string,
    data: $TSFixMe,
    status?: number
  ): IPromise<never> {
    const message = `[ServicesManager] Services: Failed to retrieve service item`
    trackError(message, {
      data: {
        serviceId,
        responseCode: status,
        data,
      },
    })
    return Promise.reject(new Error(`${message}`))
  }
}

angular
  .module('webregister.managers')
  .factory('servicesManager', ServicesManager)
